import React, { useState, useEffect } from 'react';
import { TextField, Button, Grid, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const initialState = {
    fibra: '',
    tipoFibra: '',
    numero1: '',
    numero2: '',
    titulo1: '',
    titulo2: '',
    materiaPrima: ''
};

const MateriaPrimaForm = () => {
    const [form, setForm] = useState(initialState);
    const [materiasPrimas, setMateriasPrimas] = useState([]);
    const [editId, setEditId] = useState(null);
    const [mensaje, setMensaje] = useState('');
    const [deleteId, setDeleteId] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);

    const fibraOptions = ['Algodon', 'Poliester', 'Polialgodon', 'Elastomero', 'Spun(Hilado)', 'Core-Spun'];
    
    const getTipoFibraOptions = (fibra) => {
        switch(fibra) {
            case 'Algodon':
            case 'Spun(Hilado)':
            case 'Core-Spun':
                return ['OpenEnd', 'Cardado', 'Peinado', 'Pima/Supima', 'Reciclado', 'Vortex', 'Premium'];
            case 'Poliester':
                return ['DTY(Texturizado)', 'Microfibra', 'FDY(Liso)'];
            case 'Polialgodon':
                return ['Cardado', 'Peinado', 'Jaspe'];
            case 'Elastomero':
                return ['Desnudo', 'Recubierto'];
            default:
                return [];
        }
    };

    useEffect(() => {
        fetchMateriasPrimas();
    }, []);

    const fetchMateriasPrimas = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/materias-primas`);
            const data = await res.json();
            setMateriasPrimas(data.data || data);
        } catch (err) {
            setMensaje('Error al cargar materias primas');
        }
    };

    const handleChangeFibra = (e) => {
        setForm({ ...form, fibra: e.target.value, tipoFibra: '', numero1: '', numero2: '', titulo1: '', titulo2: '' });
    };

    const handleChangeNumero = (field, value) => {
        setForm({ ...form, [field]: value });
    };

    const construirMateriaPrima = () => {
        const { fibra, tipoFibra, numero1, numero2, titulo1, titulo2 } = form;
        if (!fibra) return '';
        
        // Construir el nombre base con tipo de fibra si existe
        let nombreBase = fibra;
        if (tipoFibra) {
            nombreBase += ` ${tipoFibra}`;
        }
        
        // Caso especial para Polialgodon: numero1-numero2 titulo1/titulo2 Ne
        if (fibra === 'Polialgodon') {
            let resultado = nombreBase;
            if (numero1 && numero2) {
                resultado += ` ${numero1}-${numero2}`;
            } else if (numero1) {
                resultado += ` ${numero1}`;
            } else if (numero2) {
                resultado += ` ${numero2}`;
            }
            if (titulo1 && titulo2) resultado += ` ${titulo1}/${titulo2} Ne`;
            else if (titulo1) resultado += ` ${titulo1} Ne`;
            return resultado;
        }
        
        const unidad = (fibra === 'Poliester' || fibra === 'Elastomero') ? 'denier' : 'Ne';
        
        if (numero1 && numero2) {
            return `${nombreBase} ${numero1}/${numero2} ${unidad}`;
        } else if (numero1) {
            return `${nombreBase} ${numero1} ${unidad}`;
        }
        return nombreBase;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMensaje('');
        
        const materiaPrimaCompleta = construirMateriaPrima();
        
        if (!materiaPrimaCompleta || !form.fibra) {
            setMensaje('Por favor completa la fibra');
            return;
        }

        try {
            const payload = { materiaPrima: materiaPrimaCompleta };
            let res, data;
            
            if (editId) {
                res = await fetch(`${process.env.REACT_APP_API_URL}/materias-primas/${editId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
                data = await res.json();
            } else {
                res = await fetch(`${process.env.REACT_APP_API_URL}/materias-primas`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
                data = await res.json();
            }
            
            if (res.ok) {
                setMensaje(editId ? 'Materia prima actualizada' : 'Materia prima creada correctamente');
                setForm(initialState);
                setEditId(null);
                fetchMateriasPrimas();
            } else {
                setMensaje(data.message || 'Error al guardar materia prima');
            }
        } catch (err) {
            setMensaje('Error de conexión');
        }
    };

    const handleEdit = (mp) => {
        // Parsear la materia prima para extraer los valores
        const texto = mp.materiaPrima;
        let fibra = '';
        let tipoFibra = '';
        let numero1 = '';
        let numero2 = '';
        let titulo1 = '';
        let titulo2 = '';
        
        // Identificar la fibra
        for (const f of fibraOptions) {
            if (texto.startsWith(f)) {
                fibra = f;
                break;
            }
        }
        
        // Extraer tipo de fibra si existe
        if (fibra) {
            const tipoOptions = getTipoFibraOptions(fibra);
            for (const tipo of tipoOptions) {
                if (texto.includes(tipo)) {
                    tipoFibra = tipo;
                    break;
                }
            }
        }
        
        // Caso especial para Polialgodon: "Polialgodon Cardado 65-35 30/1 Ne"
        if (fibra === 'Polialgodon') {
            // Buscar patrón numero-numero
            const matchComposicion = texto.match(/(\d+)-(\d+)/);
            if (matchComposicion) {
                numero1 = matchComposicion[1];
                numero2 = matchComposicion[2];
            } else {
                // Si no hay guion, buscar solo un número antes del título
                const matchSingle = texto.match(/[^\d](\d+)\s+\d+\//);
                if (matchSingle) numero1 = matchSingle[1];
            }
            // Buscar título
            const matchTitulo = texto.match(/(\d+)(?:\/(\d+))?\s*Ne/);
            if (matchTitulo) {
                titulo1 = matchTitulo[1] || '';
                titulo2 = matchTitulo[2] || '';
            }
        } else {
            // Extraer números si existen
            const match = texto.match(/(\d+)(?:\/(\d+))?/);
            if (match) {
                numero1 = match[1] || '';
                numero2 = match[2] || '';
            }
        }
        
        setForm({ fibra, tipoFibra, numero1, numero2, titulo1, titulo2, materiaPrima: texto });
        setEditId(mp._id);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/materias-primas/${deleteId}`, {
                method: 'DELETE'
            });
            if (res.ok) {
                setMensaje('Materia prima eliminada');
                setDeleteId(null);
                setOpenDialog(false);
                fetchMateriasPrimas();
            } else {
                setMensaje('Error al eliminar materia prima');
            }
        } catch (err) {
            setMensaje('Error de conexión');
        }
    };

    const mostrarUnidad = () => {
        if (!form.fibra) return '';
        return (form.fibra === 'Poliester' || form.fibra === 'Elastomero') ? 'denier' : 'Ne';
    };

    return (
        <div>
            <Paper sx={{ p: 3, maxWidth: 700, margin: 'auto', mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                    {editId ? 'Editar materia prima' : 'Ingresar nueva materia prima'}
                </Typography>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                select
                                label="Fibra"
                                name="fibra"
                                value={form.fibra}
                                onChange={handleChangeFibra}
                                required
                                fullWidth
                            >
                                <MenuItem value=""><em>Seleccione una fibra</em></MenuItem>
                                {fibraOptions.map(option => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        
                        {form.fibra && (
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    label="Tipo de Fibra"
                                    name="tipoFibra"
                                    value={form.tipoFibra}
                                    onChange={(e) => setForm({ ...form, tipoFibra: e.target.value })}
                                    fullWidth
                                >
                                    <MenuItem value=""><em>Seleccione tipo</em></MenuItem>
                                    {getTipoFibraOptions(form.fibra).map(option => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        )}
                        
                        {form.fibra && form.fibra === 'Polialgodon' && (
                            <>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TextField
                                            label="Poliester"
                                            name="numero1"
                                            type="number"
                                            value={form.numero1}
                                            onChange={(e) => handleChangeNumero('numero1', e.target.value)}
                                            fullWidth
                                            inputProps={{ min: 0 }}
                                        />
                                        <TextField
                                            label="Algodon"
                                            name="numero2"
                                            type="number"
                                            value={form.numero2}
                                            onChange={(e) => handleChangeNumero('numero2', e.target.value)}
                                            fullWidth
                                            inputProps={{ min: 0 }}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TextField
                                            label="Título 1"
                                            name="titulo1"
                                            type="number"
                                            value={form.titulo1}
                                            onChange={(e) => handleChangeNumero('titulo1', e.target.value)}
                                            fullWidth
                                            inputProps={{ min: 0 }}
                                        />
                                        <Typography variant="h6">/</Typography>
                                        <TextField
                                            label="hilos"
                                            name="titulo2"
                                            type="number"
                                            value={form.titulo2}
                                            onChange={(e) => handleChangeNumero('titulo2', e.target.value)}
                                            fullWidth
                                            inputProps={{ min: 0 }}
                                        />
                                        <Typography variant="h6" sx={{ minWidth: 80 }}>
                                            Ne
                                        </Typography>
                                    </Box>
                                </Grid>
                            </>
                        )}
                        
                        {form.fibra && !['Polialgodon'].includes(form.fibra) && (
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TextField
                                        label="Título 1"
                                        name="numero1"
                                        type="number"
                                        value={form.numero1}
                                        onChange={(e) => handleChangeNumero('numero1', e.target.value)}
                                        fullWidth
                                        inputProps={{ min: 0 }}
                                    />
                                    <Typography variant="h6">/</Typography>
                                    <TextField
                                        label="hilos"
                                        name="numero2"
                                        type="number"
                                        value={form.numero2}
                                        onChange={(e) => handleChangeNumero('numero2', e.target.value)}
                                        fullWidth
                                        inputProps={{ min: 0 }}
                                    />
                                    <Typography variant="h6" sx={{ minWidth: 80 }}>
                                        {mostrarUnidad()}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                        
                        {form.fibra && (
                            <Grid item xs={12}>
                                <TextField
                                    label="Vista previa"
                                    value={construirMateriaPrima()}
                                    fullWidth
                                    InputProps={{ readOnly: true }}
                                    sx={{ 
                                        backgroundColor: '#424242',
                                        '& .MuiInputBase-input': {
                                            color: '#fff'
                                        }
                                    }}
                                />
                            </Grid>
                        )}
                        
                        <Grid item xs={12}>
                            <Button type="submit" variant="contained" color="primary" fullWidth>
                                {editId ? 'Actualizar materia prima' : 'Guardar materia prima'}
                            </Button>
                        </Grid>
                        {mensaje && (
                            <Grid item xs={12}>
                                <Typography color={mensaje.includes('correctamente') || mensaje.includes('actualizada') || mensaje.includes('eliminada') ? 'green' : 'error'}>
                                    {mensaje}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </form>
            </Paper>

            {/* Tabla de materias primas */}
            <TableContainer component={Paper} sx={{ maxWidth: 1200, margin: '40px auto' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Materia Prima</TableCell>
                            <TableCell>Fecha Creación</TableCell>
                            <TableCell>Editar</TableCell>
                            <TableCell>Eliminar</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {materiasPrimas.map((mp) => (
                            <TableRow key={mp._id}>
                                <TableCell>{mp.materiaPrima}</TableCell>
                                <TableCell>{mp.createdAt ? new Date(mp.createdAt).toLocaleString() : ''}</TableCell>
                                <TableCell>
                                    <IconButton color="primary" onClick={() => handleEdit(mp)}>
                                        <EditIcon />
                                    </IconButton>
                                </TableCell>
                                <TableCell>
                                    <IconButton color="error" onClick={() => { setDeleteId(mp._id); setOpenDialog(true); }}>
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Diálogo de confirmación para eliminar */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>¿Eliminar materia prima?</DialogTitle>
                <DialogContent>
                    ¿Estás seguro de que deseas eliminar esta materia prima?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                    <Button color="error" onClick={handleDelete}>Eliminar</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default MateriaPrimaForm;
